/**
 * HasLastKnownPositionCondition - behavior tree condition checking for recent target position.
 */

import { EngineClass } from '../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../BehaviorNode.js';
import { ConditionEvaluator, type ConditionEvaluatorOptions } from '../ConditionEvaluator.js';

import type { Blackboard } from '../../blackboard/Blackboard.js';
import type * as THREE from 'three';

export interface HasLastKnownPositionConditionOptions extends ConditionEvaluatorOptions {
  lastKnownPositionKey?: string;
  maxAge?: number;
}

@EngineClass('HasLastKnownPositionCondition')
export class HasLastKnownPositionCondition extends ConditionEvaluator {
  private lastKnownPositionKey: string;
  private maxAge?: number;

  constructor(options: HasLastKnownPositionConditionOptions = {}) {
    super({
      name: 'HasLastKnownPosition',
      ...options
    });

    this.lastKnownPositionKey = options.lastKnownPositionKey ?? 'LastKnownPlayerPosition';
    this.maxAge = options.maxAge;
  }

  protected override async onEvaluate(blackboard: Blackboard): Promise<BehaviorStatus> {
    // Check if we have a last known position
    const lastKnownPosition = blackboard.getValue<THREE.Vector3>(this.lastKnownPositionKey);

    if (!lastKnownPosition) {
      return BehaviorStatus.Failure;
    }

    // If max age is specified, check the timestamp
    if (this.maxAge !== undefined) {
      const timestampKey = `${this.lastKnownPositionKey}_Timestamp`;
      const timestamp = blackboard.getValue<number>(timestampKey);

      if (timestamp !== undefined) {
        const currentTime = Date.now() / 1000;
        const age = currentTime - timestamp;

        if (age > this.maxAge) {
          // Position is too old, clear it
          blackboard.setValue(this.lastKnownPositionKey, null);
          blackboard.setValue(timestampKey, null);
          return BehaviorStatus.Failure;
        }
      }
    }

    return BehaviorStatus.Success;
  }
}